import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiUrl } from '../api';

export interface LatLng {
  lat: number;
  lng: number;
}

interface Props {
  address?: string;
  center?: LatLng | null;
  marker?: LatLng | null;
  onSelect?: (lat: number, lng: number, address: string) => void;
  heightClass?: string;
  className?: string;
  zoom?: number;
}

/**
 * Leaflet + OpenStreetMap replacement for the Google Maps component.
 *
 * The Google browser key on this project returns UrlAuthenticationCommonError
 * for the JavaScript API, while server-side geocoding still works. Leaflet
 * needs no key, so every staff/client map keeps working without touching the
 * Cloud billing account.
 */
const LeafletMap: React.FC<Props> = ({
  address,
  center = null,
  marker = null,
  onSelect,
  heightClass = 'h-64',
  className = '',
  zoom = 17,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const tileRef = useRef<L.TileLayer | null>(null);
  const [position, setPosition] = useState<LatLng | null>(center);
  const [error, setError] = useState('');
  const [satellite, setSatellite] = useState(false);
  const [loading, setLoading] = useState(!center && !!address);

  useEffect(() => {
    if (center) {
      setPosition(center);
      return;
    }
    if (!address) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetch(apiUrl(`/api/geocode?address=${encodeURIComponent(address)}`))
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data?.status === 'OK' && typeof data.lat === 'number' && typeof data.lng === 'number') {
          setPosition({ lat: data.lat, lng: data.lng });
        } else {
          setError('Address could not be located.');
        }
      })
      .catch(() => {
        if (!cancelled) setError('Geocoding service unavailable.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [address, center]);

  useEffect(() => {
    if (!containerRef.current || !position) return;
    if (!mapRef.current) {
      const map = L.map(containerRef.current, {
        center: [position.lat, position.lng],
        zoom,
        zoomControl: true,
        attributionControl: true,
      });
      mapRef.current = map;
      const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 20,
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);
      tileRef.current = tiles;

      map.on('click', async (event: L.LeafletMouseEvent) => {
        if (!onSelect) return;
        const { lat, lng } = event.latlng;
        let label = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`,
            { headers: { Accept: 'application/json' } },
          );
          const data = await res.json();
          if (data?.display_name) label = data.display_name;
        } catch {
          /* keep coordinate label */
        }
        onSelect(lat, lng, label);
      });
    } else {
      mapRef.current.setView([position.lat, position.lng], zoom);
    }

    const map = mapRef.current;
    if (!markerRef.current) {
      const icon = L.divIcon({
        className: '',
        html: '<div style="width:18px;height:18px;border-radius:9999px;background:#2D5A27;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>',
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      });
      markerRef.current = L.marker([position.lat, position.lng], { icon }).addTo(map);
    } else {
      markerRef.current.setLatLng([position.lat, position.lng]);
    }

    return () => {
      // Map instance is kept across address changes; removed on unmount below.
    };
  }, [position, onSelect, zoom]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
        tileRef.current = null;
      }
    };
  }, []);

  const toggleLayer = () => {
    if (!mapRef.current) return;
    const next = !satellite;
    setSatellite(next);
    if (tileRef.current) mapRef.current.removeLayer(tileRef.current);
    const url = next
      ? 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const attribution = next
      ? 'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics'
      : '&copy; OpenStreetMap contributors';
    tileRef.current = L.tileLayer(url, { maxZoom: 20, attribution }).addTo(mapRef.current);
  };

  if (error) {
    return (
      <div className={`${heightClass} rounded-xl bg-amber-50 border border-amber-200 p-6 flex flex-col items-center justify-center text-center ${className}`}>
        <p className="text-sm font-black text-amber-800">Map location unavailable</p>
        <p className="text-xs text-amber-700 mt-1">{error}</p>
        {address && (
          <a
            className="mt-3 text-[10px] font-black uppercase tracking-widest text-palm hover:underline"
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noreferrer"
          >
            Open address in Google Maps
          </a>
        )}
      </div>
    );
  }

  if (loading && !position) {
    return <div className={`${heightClass} rounded-xl bg-stone-100 animate-pulse ${className}`} />;
  }

  return (
    <div className={`relative ${heightClass} rounded-xl overflow-hidden border border-stone-200 ${className}`}>
      <div ref={containerRef} className="absolute inset-0" />
      <button
        type="button"
        onClick={toggleLayer}
        className="absolute top-3 right-3 z-[500] px-2.5 py-1.5 rounded-lg bg-white/95 border border-stone-200 text-[9px] font-black uppercase tracking-widest text-stone-700 shadow-sm hover:bg-white"
      >
        {satellite ? 'Street map' : 'Satellite'}
      </button>
      <a
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || (position ? `${position.lat},${position.lng}` : ''))}`}
        target="_blank"
        rel="noreferrer"
        className="absolute bottom-3 right-3 z-[500] px-2.5 py-1.5 rounded-lg bg-bark/90 text-white text-[9px] font-black uppercase tracking-widest shadow-sm hover:bg-bark"
      >
        Directions
      </a>
    </div>
  );
};

export default LeafletMap;
